import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Coins, ArrowRight } from 'lucide-react'
import { useDashboardTokenUsage } from '@/hooks/useDashboardTokenUsage'
import { useAuth } from '@/contexts/AuthContext'
import { useAgency } from '@/hooks/useAgency'
import { hasAdminPrivileges, isSuperAdmin } from '@/utils/permissions'
import { Skeleton } from '@/components/ui/skeleton'

/** License-based model: token UI hidden. Set to true to re-enable. */
const TOKEN_UI_ENABLED = false

export const DashboardTokenUsage = () => {
  if (!TOKEN_UI_ENABLED) return null

  const { t } = useTranslation(['dashboard', 'common'])
  const { user } = useAuth()
  const { agency } = useAgency(user?.agency_id)
  const { usage, loading, error } = useDashboardTokenUsage()

  // Hide tokens for enterprise agencies (super_admin still sees them)
  if (agency?.hide_tokens && !isSuperAdmin(user)) {
    return null
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-framed-neutral-100 rounded-2xl p-5 mb-6">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      </div>
    )
  }

  // Don't render if error, no data, or no projects with usage
  if (error || !usage || usage.projectCount === 0) {
    return null
  }

  const isAdmin = hasAdminPrivileges(user)

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-framed-neutral-800">
            {t('dashboard:tokenUsage.title', 'Token Usage')}
          </h2>
        </div>
        {isAdmin && (
          <Link
            to="/tokens"
            className="text-sm text-framed-yellow-600 hover:text-framed-yellow-700 flex items-center gap-1"
          >
            {t('dashboard:tokenUsage.manageTokens', 'Manage tokens')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* This Month */}
        <div className="bg-framed-neutral-50 rounded-lg p-4">
          <p className="text-[11px] font-medium tracking-wide uppercase text-framed-neutral-500 mb-1">
            {t('dashboard:tokenUsage.thisMonth', 'This Month')}
          </p>
          <p className="text-2xl font-bold text-framed-neutral-800">
            {usage.totalMonthUsage.toLocaleString()}
          </p>
          <p className="text-xs text-framed-neutral-400">
            {t('dashboard:tokenUsage.tokensUsed', 'tokens used')}
          </p>
        </div>

        {/* All Time */}
        <div className="bg-framed-neutral-50 rounded-lg p-4">
          <p className="text-[11px] font-medium tracking-wide uppercase text-framed-neutral-500 mb-1">
            {t('dashboard:tokenUsage.allTime', 'All Time')}
          </p>
          <p className="text-2xl font-bold text-framed-neutral-800">
            {usage.totalAllTimeUsage.toLocaleString()}
          </p>
          <p className="text-xs text-framed-neutral-400">
            {t('dashboard:tokenUsage.tokensUsed', 'tokens used')}
          </p>
        </div>

        {/* Projects */}
        <div className="bg-framed-neutral-50 rounded-lg p-4">
          <p className="text-[11px] font-medium tracking-wide uppercase text-framed-neutral-500 mb-1">
            {t('dashboard:tokenUsage.projects', 'Projects')}
          </p>
          <p className="text-2xl font-bold text-framed-neutral-800">
            {usage.projectCount}
          </p>
          <p className="text-xs text-framed-neutral-400">
            {t('dashboard:tokenUsage.withTokenUsage', 'with token usage')}
          </p>
        </div>

        {/* Top Project */}
        {usage.projects.length > 0 && (
          <div className="bg-amber-50 rounded-lg p-4">
            <p className="text-sm text-amber-600 mb-1">
              {t('dashboard:tokenUsage.topProject', 'Top Project')}
            </p>
            <p className="text-sm font-semibold text-framed-neutral-800 truncate">
              {usage.projects[0].project_name}
            </p>
            <p className="text-xs text-amber-600">
              {usage.projects[0].current_month.toLocaleString()} {t('dashboard:tokenUsage.thisMonthShort', 'this month')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
